import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { Upload } from "lucide-react";

const PhotovoltaicApplicationForm: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [timer, setTimer] = useState(0);
  const [captchaUrl, setCaptchaUrl] = useState(
    `/api/placeholder/200/50?timestamp=${new Date().getTime()}`
  );
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [timeTaken, setTimeTaken] = useState<number | null>(null); // State to hold total time taken

  const [formData, setFormData] = useState({
    email: "",
    confirmEmail: "",
    name: "",
    surname: "",
    idSeries: "",
    idNumber: "",
    validFrom: "",
    validUntil: "",
    cnp: "",
    address: "",
    county: "",
    phone: "",
    documents: {
      idCard: null,
      fiscalCertificate: null,
      localTaxesCertificate: null,
      propertyDeed: null,
      collectiveDeed: null, // Optional document
    },
    captcha: "",
    agreements: {
      program: false,
      dataAccuracy: false,
      communication: false,
      termsAndConditions: false,
    },
  });

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (showForm && timer < 240 && timeTaken === null) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showForm, timer, timeTaken]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      documents: {
        ...prevState.documents,
        [name]: files ? files[0] : null,
      },
    }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      agreements: {
        ...prevState.agreements,
        [name]: checked,
      },
    }));
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  const regenerateCaptcha = () => {
    setCaptchaUrl(`/api/placeholder/200/50?timestamp=${new Date().getTime()}`);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!recaptchaValue) {
      alert("Please complete the CAPTCHA");
      return;
    }

    console.log(formData);
    console.log("reCAPTCHA value:", recaptchaValue);
  };

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return Boolean(
          formData.email &&
            formData.confirmEmail &&
            formData.email === formData.confirmEmail &&
            formData.captcha
        );
      case 2:
        return Boolean(
          formData.name &&
            formData.surname &&
            formData.idSeries &&
            formData.idNumber &&
            formData.validFrom &&
            formData.validUntil &&
            formData.cnp &&
            formData.address &&
            formData.county &&
            formData.phone
        );
      case 3:
        // Verifică doar primele 4 documente (al cincilea, collectiveDeed, este opțional)
        return Boolean(
          formData.documents.idCard &&
          formData.documents.fiscalCertificate &&
          formData.documents.localTaxesCertificate &&
          formData.documents.propertyDeed
        );
      case 4:
        return Object.values(formData.agreements).every((agreement) => agreement);
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (isStepComplete(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else if (currentStep === 4) {
        // Stop the timer and calculate the time taken
        setTimeTaken(timer); // Save the total time taken
        setTimer(0); // Stop the timer
      }
    }
  };

  const submitForm = () => {
    if (isStepComplete(4)) {
      setTimeTaken(timer); // Salvează timpul la momentul apasării butonului
      window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll la începutul paginii
      console.log("Form submitted!", formData); // Logica de trimitere a formularului
    }
  };

  const displayFormData = () => {
    return (
      <div className="mt-4">
        <h3 className="text-lg font-bold mb-2">Date introduse</h3>
        <ul className="list-disc list-inside">
          <li>Email: {formData.email}</li>
          <li>Nume: {formData.name}</li>
          <li>Prenume: {formData.surname}</li>
          <li>Serie CI: {formData.idSeries}</li>
          <li>Număr CI: {formData.idNumber}</li>
          <li>Valabil de la: {formData.validFrom}</li>
          <li>Valabil până la: {formData.validUntil}</li>
          <li>CNP: {formData.cnp}</li>
          <li>Adresă: {formData.address}</li>
          <li>Județ: {formData.county}</li>
          <li>Telefon: {formData.phone}</li>
        </ul>

        <h3 className="text-lg font-bold mt-4 mb-2">Documente încărcate</h3>
        <ul className="list-disc list-inside">
          {Object.entries(formData.documents).map(([key, file]) => (
            <li key={key}>
              {key}: {file ? (file as File).name : "Nu a fost încărcat"}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!showForm) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">DepunereFotovoltaice</h1>
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
          <p>Sesiunea este deschisă pentru deponent de tip Persoană Fizică</p>
          <p>
            Regiunea activă: SUD - EST. Se permite depunerea doar pentru
            județele: Brăila, Buzău, Constanța, Galați, Tulcea, Vrancea.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center"
          >
            Depunere Persoane Fizice
          </button>
          <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center">
            Depunere Unități de Cult
          </button>
        </div>
      </div>
    );
  }

  if (timeTaken !== null) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Formular completat</h1>
        <p className="text-lg">
          Ai finalizat formularul în {Math.floor(timeTaken / 60)} minute și{" "}
          {timeTaken % 60} secunde.
        </p>

        {/* Afișarea datelor introduse */}
        {displayFormData()}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">
        Formular de depunere pentru persoane fizice
      </h1>
      <div className="text-right mb-4">
        <p className="text-lg font-semibold">
          Timp scurs: {Math.floor(timer / 60)}:{timer % 60 < 10 ? "0" : ""}
          {timer % 60}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1 */}
        <div className="relative">
          {" "}
          {/* Add a relative container */}
          <div
            className={`${
              currentStep !== 1 && "opacity-50 pointer-events-none"
            }`}
          >
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
              <h2 className="font-bold">Pas 1</h2>
              <p className="mt-1">Confirmare adresă de email depunere</p>
              <p className="mt-1">
                Introduceți adresă de email in baza căreia se va face depunerea.
                Adresa de email trebuie să fie identică în câmpul de confirmare
                adresă email.
              </p>
            </div>

            {/* Email Fields */}
            <div className="space-y-4 mt-4">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700">
                  Adresă de email:
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  required
                />
              </div>

              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700">
                  Confirmare Adresă de email:
                </label>
                <input
                  type="email"
                  name="confirmEmail"
                  value={formData.confirmEmail}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  required
                />
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Introduceți codul CAPTCHA:
              </label>
              <div className="flex items-center mt-2">
                <div className="flex-shrink-0">
                  <div className="border border-gray-300 h-auto w-32 flex items-center justify-center bg-gray-200 p-2">
                    <img src="https://i.imgur.com/9z96aLf.png" />
                  </div>
                </div>

                <div className="ml-4 flex-1 max-w-md">
                  <input
                    type="text"
                    name="captcha"
                    value={formData.captcha}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Regenerate CAPTCHA Button */}
            <div className="flex justify-start mt-4 max-w-md">
              <button
                type="button"
                onClick={regenerateCaptcha}
                className="px-4 py-2 bg-blue-500 text-white rounded-md"
              >
                Regenerare cod
              </button>
            </div>

            {/* Pasul Următor Button Positioned Right */}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
              <button
                type="button"
                onClick={nextStep}
                className={`px-4 py-2 bg-blue-500 text-white rounded-md ${
                  !isStepComplete(1)
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-600"
                }`}
                disabled={!isStepComplete(1)}
              >
                Pasul următor
              </button>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div
          className={`mt-8 ${
            currentStep !== 2 && "opacity-50 pointer-events-none"
          }`}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
            <h2 className="font-bold">Pas 2</h2>
            <p>Date BI/CI</p>
            <p>
              Introduceți datele actului de identitate al solicitantului. Data
              valabilității actului se introduce în format zi.luna.an
              (zz.ll.aaaa).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nume:
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Prenume:
              </label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
              Serie Act identitate:
              </label>
              <input
                type="text"
                name="idSeries"
                value={formData.idSeries}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
              Număr Act identitate:
              </label>
              <input
                type="text"
                name="idNumber"
                value={formData.idNumber}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
              Valabil de la:
              </label>
              <input
                type="text"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
              Valabil până la:
              </label>
              <input
                type="text"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CNP:
              </label>
              <input
                type="text"
                name="cnp"
                value={formData.cnp}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
              Adresă domiciliu (Localitate, stradă, număr):
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Județ:
              </label>
              <input
                type="text"
                name="county"
                value={formData.county}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Telefon:
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                required
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={nextStep}
              className={`mt-4 px-4 py-2 bg-blue-500 text-white rounded-md ${
                !isStepComplete(2)
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-600"
              }`}
              disabled={!isStepComplete(2)}
            >
              Pasul următor
            </button>
          </div>
        </div>

        {/* Step 3 */}
        <div
          className={`mt-8 ${
            currentStep !== 3 && "opacity-50 pointer-events-none"
          }`}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
            <h2 className="font-bold">Pas 3</h2>
            <p>Documente necesare</p>
            <p>
              Încărcați toate documentele necesare pentru a putea continua cu
              pasul 4 de depunere. Dimensiunea maximă a unui fișer este de 5 Mb.
            </p>
          </div>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                1) Actul de identitate al solicitantului, valabil la data
                înscrierii în aplicație; - (obligatoriu)
              </label>
              <input
                type="file"
                name="idCard"
                onChange={handleFileUpload}
                className="mt-1 block w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                2) Certificatul de atestare fiscala privind obligatiile de plata
                catre bugetul de stat, emis pe numele solicitantului de catre
                organul teritorial de specialitate al Ministerului Finantelor,
                in termen de valabilitate la data inscrierii in aplicatie; -
                (obligatoriu)
              </label>
              <input
                type="file"
                name="fiscalCertificate"
                onChange={handleFileUpload}
                className="mt-1 block w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                3) Certificatul de atestare fiscala privind impozitele si taxele
                locale si alte venituri ale bugetului local, emis pe numele
                solicitantului de catre autoritatea publica locala in a carei
                raza teritoriala isi are domiciliul, in termen de valabilitate
                la data inscrierii in aplicatie; - (obligatoriu)
              </label>
              <input
                type="file"
                name="localTaxesCertificate"
                onChange={handleFileUpload}
                className="mt-1 block w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                4) Extras de carte funciara; - (obligatoriu)
              </label>
              <input
                type="file"
                name="propertyDeed"
                onChange={handleFileUpload}
                className="mt-1 block w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                5) Copia cartii funciare colective; - (opțional)
              </label>
              <input
                type="file"
                name="collectiveDeed"
                onChange={handleFileUpload}
                className="mt-1 block w-full"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={nextStep}
              className={`mt-4 px-4 py-2 bg-blue-500 text-white rounded-md ${
                !isStepComplete(3)
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-600"
              }`}
              disabled={!isStepComplete(3)}
            >
              Pasul următor
            </button>
          </div>
        </div>

        {/* Step 4 */}
        <div
          className={`mt-8 ${currentStep !== 4 ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
            <h2 className="font-bold">Pas 4</h2>
            <p>Validare și depunere</p>
            <p>
              Introduceți corect codul CAPTCHA și bifați că sunteți de acord cu termenii și condițiile pentru a continua depunerea proiectului.
            </p>
          </div>

          {/* CAPTCHA input - doar pentru afișare, nu este validat */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Introduceți codul CAPTCHA:
            </label>
            <div className="flex items-center mt-2">
              <div className="flex-shrink-0">
                <div className="border border-gray-300 h-auto w-32 flex items-center justify-center bg-gray-200 p-2">
                  <img src="https://i.imgur.com/9z96aLf.png" />
                </div>
              </div>
              <div className="ml-4 flex-1 max-w-md">
                <input
                  type="text"
                  name="captcha"
                  value={formData.captcha}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
            </div>
          </div>

          {/* Regenerate CAPTCHA button */}
          <div className="flex justify-start mt-4 max-w-md">
            <button
              type="button"
              onClick={regenerateCaptcha}
              className="px-4 py-2 bg-blue-500 text-white rounded-md"
            >
              Regenerare cod
            </button>
          </div>

          {/* Agreement checkboxes */}
          <div className="space-y-2 mt-4">
            {Object.entries(formData.agreements).map(([key, value]) => (
              <div key={key} className="flex items-center">
                <input
                  type="checkbox"
                  id={key}
                  name={key}
                  checked={value}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor={key} className="ml-2 block text-sm text-gray-900">
                  {key === "program" &&
                    "Declar că sunt de acord cu participarea la Programul privind instalarea sistemelor de panouri fotovoltaice pentru producerea de energie electrică, în vederea acoperirii necesarului de consum și livrării surplusului în rețeaua națională."}
                  {key === "dataAccuracy" &&
                    "Declar că informațiile furnizate în cadrul proiectului sunt corecte și complete."}
                  {key === "communication" &&
                    "Sunt de acord să fiu contactat prin e-mail sau telefon în legătură cu acest proiect."}
                  {key === "termsAndConditions" &&
                    "Am citit și sunt de acord cu termenii și condițiile programului."}
                </label>
              </div>
            ))}
          </div>

          {/* Submit button */}
          <div className="mt-8">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={submitForm}
                className={`mt-4 px-4 py-2 bg-blue-500 text-white rounded-md ${
                  !isStepComplete(4)
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-600"
                }`}
                disabled={!isStepComplete(4)}
              >
                Depune
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PhotovoltaicApplicationForm;
